import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useChild } from "../lib/childContext";
import { toast } from "sonner";
import { ArrowUpRight, Lock } from "lucide-react";
import Brand from "../components/Brand";

export default function Accept() {
  const { kind, token } = useParams();
  const { user, loading } = useAuth();
  const { refresh } = useChild();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const endpoint =
      kind === "coparent"
        ? `/coparent/invite/${token}`
        : `/workspace-invites/${token}`;
    api
      .get(endpoint)
      .then((r) => setPreview(r.data))
      .catch((err) =>
        setError(err.response?.data?.detail || "This invite isn't here anymore.")
      );
  }, [kind, token]);

  const accept = async () => {
    setBusy(true);
    try {
      if (kind === "coparent") {
        await api.post(`/coparent/accept/${token}`);
        await refresh();
        toast("Welcome. You are held here too.");
        navigate("/home");
      } else {
        const r = await api.post(`/workspace-invites/accept/${token}`);
        toast("Welcome to the center.");
        if (user?.role === "admin") {
          navigate("/workspace");
        } else {
          await refresh();
          navigate("/home");
        }
      }
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't accept the invite right now.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="px-6 sm:px-12 py-8 flex items-center justify-between">
        <Link to="/" className="flex items-center">
          <Brand size="sm" dataTestId="brand-accept" />
        </Link>
        <span className="trust-pill">
          <Lock size={11} strokeWidth={1.5} />
          Encrypted
        </span>
      </div>

      <main className="flex-1 flex items-center justify-center px-6 sm:px-10 pb-24">
        <div className="w-full max-w-md fade-up">
          {error ? (
            <div data-testid="accept-error">
              <span className="eyebrow">A quiet pause</span>
              <h1 className="mt-4 font-serif text-4xl leading-tight tracking-tight">
                This invite isn&rsquo;t here anymore.
              </h1>
              <p className="mt-6 text-[#6A6863] font-light">{error}</p>
              <Link to="/" className="btn-ghost mt-10 inline-flex">
                Back to the calm
              </Link>
            </div>
          ) : !preview ? (
            <p className="font-serif italic text-[#6A6863]">a quiet moment…</p>
          ) : preview.accepted ? (
            <div data-testid="accept-already">
              <span className="eyebrow">Already accepted</span>
              <h1 className="mt-4 font-serif text-4xl leading-tight tracking-tight">
                This invite has gently completed its journey.
              </h1>
              <Link to="/home" className="btn-primary mt-10 inline-flex">
                Enter Alouvya
                <ArrowUpRight size={16} strokeWidth={1.5} />
              </Link>
            </div>
          ) : (
            <div data-testid="accept-preview">
              <span className="eyebrow">
                {kind === "coparent" ? "Co-parent invite" : `Invite · ${preview.role_to_grant}`}
              </span>
              <h1 className="mt-4 font-serif text-4xl sm:text-5xl leading-tight tracking-tight">
                {kind === "coparent" ? (
                  <>
                    {preview.invited_by_name}{" "}
                    <em className="italic text-[#8A9984]">
                      would like you to hold {preview.child_name}{" "}
                    </em>
                    too.
                  </>
                ) : (
                  <>
                    {preview.invited_by_name}{" "}
                    <em className="italic text-[#8A9984]">
                      has opened a quiet door to {preview.workspace_name}.
                    </em>
                  </>
                )}
              </h1>
              <p className="mt-6 text-[#6A6863] font-light">
                Expires {new Date(preview.expires_at).toLocaleDateString()}.
              </p>

              {loading ? (
                <p className="mt-10 font-serif italic text-[#6A6863]">a moment…</p>
              ) : !user ? (
                <div className="mt-10 flex flex-wrap gap-3">
                  <Link
                    to="/signin"
                    state={{ from: `/accept/${kind}/${token}` }}
                    data-testid="accept-signin"
                    className="btn-primary"
                  >
                    Sign in to accept
                    <ArrowUpRight size={16} strokeWidth={1.5} />
                  </Link>
                  <Link
                    to="/signup"
                    state={{ from: `/accept/${kind}/${token}` }}
                    data-testid="accept-signup"
                    className="btn-ghost"
                  >
                    Create an account
                  </Link>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={accept}
                  disabled={busy}
                  data-testid="accept-confirm"
                  className="btn-primary mt-10"
                >
                  {busy ? "A moment…" : "Accept gently"}
                  <ArrowUpRight size={16} strokeWidth={1.5} />
                </button>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
